import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import CountrySelector from '@/components/CountrySelector'
import CountryCodeSelector from '@/components/CountryCodeSelector'
import StateSelector from '@/components/StateSelector'
import TipCard from '@/components/TipCard'
import { Toast } from 'react-vant'
import { getStateList, type StateOption } from '@/utils/countryData'
import { APIWeb } from '@/api'

// 表单数据类型
interface ShippingAddressForm {
    country: string       // 国家/地区
    firstName: string     // 名
    lastName: string      // 姓
    phone: string         // 电话号码
    address: string       // 地址
    addressLine2: string  // 地址第二行
    state: string         // 省/区（预留）
    city: string          // 城市
    zipCode: string       // 邮编
}

export default React.memo(function AddShippingAddress() {
    const { t } = useTranslation()
    const navigate = useNavigate()

    // 国家区号
    const [countryCode, setCountryCode] = useState('+1')
    
    // 国家ID（用于查询州/省）
    const [countryId, setCountryId] = useState<number | null>(null)
    
    // 州/省列表
    const [stateList, setStateList] = useState<StateOption[]>([])
    const [loadingStates, setLoadingStates] = useState(false)

    // 表单数据
    const [formData, setFormData] = useState<ShippingAddressForm>({
        country: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        addressLine2: '',
        state: '',
        city: '',
        zipCode: ''
    })

    // 表单校验错误
    const [errors, setErrors] = useState<Partial<Record<keyof ShippingAddressForm, string>>>({})

    // 提交加载状态
    const [loading, setLoading] = useState(false)

    // 更新表单字段
    const updateField = useCallback((field: keyof ShippingAddressForm, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        // 清除该字段的错误
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }))
        }
    }, [errors])

    // 选择国家
    const handleCountryChange = useCallback((value: string, newCountryId?: number) => {
        updateField('country', value)
        setCountryId(newCountryId || null)
        // 清空州/省选择
        updateField('state', '')
        setStateList([])
    }, [updateField])
    
    // 选择州/省
    const handleStateChange = useCallback((value: string) => {
        updateField('state', value)
    }, [updateField])
    
    // 当国家ID变化时，加载州/省列表
    useEffect(() => {
        if (countryId) {
            setLoadingStates(true)
            getStateList(countryId)
                .then(states => {
                    setStateList(states)
                })
                .catch(error => {
                    console.error('加载州/省列表失败:', error)
                    setStateList([])
                })
                .finally(() => {
                    setLoadingStates(false)
                })
        } else {
            setStateList([])
        }
    }, [countryId])

    // 表单校验
    const validateForm = useCallback((): boolean => {
        const newErrors: Partial<Record<keyof ShippingAddressForm, string>> = {}

        if (!formData.country) newErrors.country = t('请选择国家或地区')
        if (!formData.firstName) newErrors.firstName = t('请输入名')
        if (!formData.lastName) newErrors.lastName = t('请输入姓')
        if (!formData.phone) newErrors.phone = t('请输入电话号码')
        if (!formData.address) newErrors.address = t('请输入地址')
        if (!formData.city) newErrors.city = t('请输入城市')
        if (!formData.zipCode) newErrors.zipCode = t('请输入邮编')

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }, [formData, t])

    // 检查表单是否填写完整
    const isFormValid = useMemo(() => {
        return (
            formData.country &&
            formData.firstName &&
            formData.lastName &&
            formData.phone &&
            formData.address &&
            formData.city &&
            formData.zipCode
        )
    }, [formData])

    // 提交表单
    const handleSubmit = useCallback(async () => {
        if (loading) return

        if (!validateForm()) {
            Toast.info(t('请完善表单信息'))
            return
        }

        try {
            setLoading(true)
            
            // 构建请求参数
            const params: AddShippingAddressRequest = {
                country: formData.country,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone_code: countryCode,
                phone_number: formData.phone,
                address_line1: formData.address,
                address_line2: formData.addressLine2 || undefined,
                province: formData.state,
                city: formData.city,
                postal_code: formData.zipCode,
            }
            
            // 调用保存邮寄地址接口
            await APIWeb.addShippingAddress(params)
            
            Toast.success(t('添加成功'))
            // 返回上一页或跳转到指定页面
            navigate(-1)
        } catch (error: any) {
            console.error('保存邮寄地址失败:', error)
            const errorMsg = error?.response?.data?.message || error?.message || t('添加失败')
            Toast.fail(errorMsg)
        } finally {
            setLoading(false)
        }
    }, [formData, countryCode, validateForm, navigate, t, loading])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={
                    <span className="text-3.75 font-medium text-black">{t('邮寄地址')}</span>
                }
            />

            <div className="bg-white px-6 py-6 pb-24">
                {/* 提示信息 */}
                <TipCard message={t('表单只能用英文填写')} />

                {/* 国家/地区 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('请选择您的国家或地区')}</div>
                    <CountrySelector
                        value={formData.country}
                        onChange={handleCountryChange}
                        placeholder={t('请选择您的国家或地区')}
                    />
                    {errors.country && <div className="text-xs text-red-500 mt-1">{errors.country}</div>}
                </div>

                {/* 姓 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('请输入您的姓氏')}</div>
                    <CustomTextInput
                        value={formData.lastName}
                        onChangeText={(text) => updateField('lastName', text)}
                        placeholder={t('例X', { example: 'Lee' })}
                        isErrored={!!errors.lastName}
                    />
                    {errors.lastName && <div className="text-xs text-red-500 mt-1">{errors.lastName}</div>}
                </div>

                {/* 名 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('请输入您的名字')}</div>
                    <CustomTextInput
                        value={formData.firstName}
                        onChangeText={(text) => updateField('firstName', text)}
                        placeholder={t('不含姓氏')}
                        isErrored={!!errors.firstName}
                    />
                    {errors.firstName && <div className="text-xs text-red-500 mt-1">{errors.firstName}</div>}
                </div>

                {/* 电话号码 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('电话号码')}</div>
                    <div className="flex items-center gap-2">
                        <div className="w-20">
                            <CountryCodeSelector
                                value={countryCode}
                                onChange={setCountryCode}
                            />
                        </div>
                        <div className="flex-1">
                            <CustomTextInput
                                value={formData.phone}
                                onChangeText={(text) => updateField('phone', text)}
                                placeholder=""
                                isErrored={!!errors.phone}
                                inputMode="numeric"
                            />
                        </div>
                    </div>
                    {errors.phone && <div className="text-xs text-red-500 mt-1">{errors.phone}</div>}
                </div>

                {/* 地址 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('地址')}</div>
                    <CustomTextInput
                        value={formData.address}
                        onChangeText={(text) => updateField('address', text)}
                        placeholder={t('例X', { example: '1 Infinite loop cupertion CA 95014' })}
                        isErrored={!!errors.address}
                    />
                    {errors.address && <div className="text-xs text-red-500 mt-1">{errors.address}</div>}
                </div>

                {/* 地址第二行 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('地址第二行')}</div>
                    <CustomTextInput
                        value={formData.addressLine2}
                        onChangeText={(text) => updateField('addressLine2', text)}
                        placeholder={t('例X', { example: '1 Infinite loop cupertion CA 95014' })}
                    />
                </div>

                {/* 省/区 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('请选择省或区')}</div>
                    <StateSelector
                        value={formData.state}
                        onChange={handleStateChange}
                        placeholder={t('请选择省或区')}
                        options={stateList}
                        disabled={!countryId || loadingStates}
                    />
                    {errors.state && <div className="text-xs text-red-500 mt-1">{errors.state}</div>}
                </div>

                {/* 城市 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('城市')}</div>
                    <CustomTextInput
                        value={formData.city}
                        onChangeText={(text) => updateField('city', text)}
                        placeholder={t('例X', { example: 'Frankfurt' })}
                        isErrored={!!errors.city}
                    />
                    {errors.city && <div className="text-xs text-red-500 mt-1">{errors.city}</div>}
                </div>

                {/* 邮编 */}
                <div className="mb-6">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('邮编')}</div>
                    <CustomTextInput
                        value={formData.zipCode}
                        onChangeText={(text) => updateField('zipCode', text)}
                        placeholder={t('例X', { example: '100009' })}
                        isErrored={!!errors.zipCode}
                    />
                    {errors.zipCode && <div className="text-xs text-red-500 mt-1">{errors.zipCode}</div>}
                </div>

                {/* 提交按钮 */}
                <div className="fixed bottom-6 left-6 right-6">
                    <CustomButton
                        onClick={handleSubmit}
                        disabled={!isFormValid || loading}
                    >
                        {loading ? t('提交中') : t('储存')}
                    </CustomButton>
                </div>
            </div>
        </PageLayout>
    )
})
